import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, ShieldCheck, Info, RefreshCw, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date, humanise, relative } from '@/lib/format';
import {
  Avatar, Badge, Button, Card, EmptyState, Field, LoadingBlock, Modal, PageHeader,
  Select, Table, TableWrap, Tabs, Td, Textarea, Th, useToast,
} from '@/components/ui';
import type { ReminderCandidate, MessageTemplate, ReminderReason } from '@shared/types';

interface QueueResponse {
  generated_at: string;
  sends_automatically: boolean;
  grace_days: number;
  counts: Record<string, number>;
  queue: ReminderCandidate[];
}

const REASON_LABEL: Record<ReminderReason, { label: string; tone: any }> = {
  overdue: { label: 'Overdue', tone: 'danger' },
  due_today: { label: 'Due today', tone: 'warn' },
  due_soon: { label: 'Due soon', tone: 'info' },
  extension_expiring: { label: 'Extension ending', tone: 'accent' },
  lease_expiring: { label: 'Lease ending', tone: 'neutral' },
  documents_missing: { label: 'Documents missing', tone: 'warn' },
  // Labelled by what we can actually observe — the record is blank — rather
  // than by a legal conclusion we are in no position to reach.
  deposit_unprotected: { label: 'Deposit not recorded', tone: 'warn' },
};

export default function Reminders() {
  const { currency, can } = useAuth();

  const [tab, setTab] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [composing, setComposing] = useState(false);

  const { data, loading, reload } = useApi<QueueResponse>('/reminders/queue');
  const { data: templates } = useApi<MessageTemplate[]>('/message-templates');

  const queue = useMemo(() => data?.queue ?? [], [data?.queue]);

  const filtered = useMemo(() => {
    if (tab === 'all') return queue;
    if (tab === 'safe') {
      // Anyone with money already in review is deliberately excluded — chasing
      // someone who has paid and is waiting on you is the fastest way to lose trust.
      return queue.filter((q) => q.pending_cents === 0 && q.reason !== 'lease_expiring');
    }
    return queue.filter((q) => q.reason === tab);
  }, [queue, tab]);

  const allSelected = filtered.length > 0 && filtered.every((q) => selected.has(q.tenant_id));

  function toggle(tenantId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId); else next.add(tenantId);
      return next;
    });
  }

  const selectedRows = queue.filter((q) => selected.has(q.tenant_id));
  const withPending = selectedRows.filter((q) => q.pending_cents > 0).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reminder queue"
        subtitle="Flagged for review — nothing is sent until you choose to send it"
        action={
          <>
            <Button icon={<RefreshCw className="h-4 w-4" />} onClick={reload} loading={loading}>
              Refresh
            </Button>
            {can('staff') && selected.size > 0 && (
              <Button variant="primary" icon={<Send className="h-4 w-4" />} onClick={() => setComposing(true)}>
                Message {selected.size}
              </Button>
            )}
          </>
        }
      />

      {/* The explicit promise about automation. */}
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-brand/8 border border-brand/25">
        <ShieldCheck className="h-5 w-5 text-brand shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-ink">This system never texts a tenant on its own.</p>
          <p className="text-muted mt-1 leading-relaxed">
            There is no scheduler. This list is calculated when you open it, using a{' '}
            {data?.grace_days ?? 3}-day grace period. Review it, pick who to contact, preview
            the wording, then send — so nobody gets chased for rent they handed over yesterday.
          </p>
        </div>
      </div>

      <Tabs
        active={tab}
        onChange={(t) => { setTab(t); setSelected(new Set()); }}
        tabs={[
          { id: 'all', label: 'Everyone flagged', count: data?.counts.total ?? 0 },
          { id: 'overdue', label: 'Overdue', count: data?.counts.overdue ?? 0 },
          { id: 'due_today', label: 'Due today', count: data?.counts.due_today ?? 0 },
          { id: 'due_soon', label: 'Due soon', count: data?.counts.due_soon ?? 0 },
          { id: 'lease_expiring', label: 'Lease ending', count: data?.counts.lease_expiring ?? 0 },
          { id: 'safe', label: 'Nothing in review' },
        ]}
      />

      {(data?.counts.awaiting_verification ?? 0) > 0 && tab !== 'safe' && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-warn/10 border border-warn/25">
          <Info className="h-4 w-4 text-warn shrink-0 mt-0.5" />
          <p className="text-sm text-muted">
            <span className="text-ink font-medium">{data?.counts.awaiting_verification} of these tenants have
            already submitted a payment</span> that is waiting for you to verify. Consider{' '}
            <Link to="/app/payments" className="link">checking those first</Link>.
          </p>
        </div>
      )}

      <Card>
        {loading ? (
          <div className="p-5"><LoadingBlock rows={6} /></div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 className="h-6 w-6" />}
            title="Nobody needs chasing"
            message="Every tenant is either paid up or not yet due. This list refreshes each time you open it."
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  {can('staff') && (
                    <Th className="w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        className="h-4 w-4 rounded border-line bg-raised accent-[rgb(var(--brand))]"
                        checked={allSelected}
                        onChange={() => setSelected(
                          allSelected ? new Set() : new Set(filtered.map((q) => q.tenant_id)),
                        )}
                      />
                    </Th>
                  )}
                  <Th>Tenant</Th>
                  <Th>Why</Th>
                  <Th>Due</Th>
                  <Th align="right">Owing</Th>
                  <Th>Last contacted</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => {
                  const key = `${q.tenant_id}-${q.charge_id ?? q.reason}`;
                  const reason = REASON_LABEL[q.reason];
                  return (
                    <tr key={key} className="row-hover">
                      {can('staff') && (
                        <Td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${q.tenant_name}`}
                            className="h-4 w-4 rounded border-line bg-raised accent-[rgb(var(--brand))]"
                            checked={selected.has(q.tenant_id)}
                            onChange={() => toggle(q.tenant_id)}
                          />
                        </Td>
                      )}
                      <Td>
                        <Link to={`/app/tenants/${q.tenant_id}`} className="flex items-center gap-2.5 min-w-0 group">
                          <Avatar name={q.tenant_name} size="sm" />
                          <div className="min-w-0">
                            <p className="font-medium truncate group-hover:text-brand transition-colors">
                              {q.tenant_name}
                            </p>
                            <p className="text-xs text-faint truncate">
                              {q.room_name ? `${q.room_name} · ` : ''}{q.property_name}
                            </p>
                          </div>
                        </Link>
                      </Td>
                      <Td>
                        <Badge tone={reason.tone} dot>{reason.label}</Badge>
                        {/*
                          The words come from the backend, which knows where
                          this landlord lets. Hard-coding England here is how
                          a landlord in Queensland was told about the wrong
                          country's law.
                        */}
                        {q.reason === 'deposit_unprotected' && (
                          <p className="text-xs text-muted mt-1 max-w-[320px]">
                            {q.days_held !== undefined && q.days_held > 0
                              ? `Held ${q.days_held} day${q.days_held === 1 ? '' : 's'}. `
                              : ''}
                            {q.deposit_note}
                          </p>
                        )}
                        {q.pending_cents > 0 && (
                          <Badge tone="warn" className="ml-1.5">Payment in review</Badge>
                        )}
                        <p className="text-xs text-faint mt-1 truncate max-w-[220px]">
                          {q.charge_description}
                        </p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {/*
                          * Missing paperwork has no date to be late against, so
                          * it says what it is rather than inventing a deadline.
                          */}
                        {q.days_offset === null || q.due_date === null ? (
                          <span className="text-muted">No deadline</span>
                        ) : (
                          <>
                            <span className={q.days_offset < 0 ? 'text-danger font-medium' : 'text-muted'}>
                              {date(q.due_date)}
                            </span>
                            <span className="block text-xs text-faint">
                              {q.days_offset === 0 ? 'today'
                                : q.days_offset < 0 ? `${Math.abs(q.days_offset)} days late`
                                : `in ${q.days_offset} days`}
                            </span>
                          </>
                        )}
                      </Td>
                      <Td align="right" className="tnum font-semibold whitespace-nowrap">
                        {q.balance_cents > 0 ? money(q.balance_cents, currency) : '—'}
                        {q.pending_cents > 0 && (
                          <span className="block text-xs text-warn font-normal">
                            {money(q.pending_cents, currency)} claimed
                          </span>
                        )}
                      </Td>
                      <Td className="text-xs text-faint whitespace-nowrap">
                        {q.last_message_at ? (
                          <>
                            {relative(q.last_message_at)}
                            <span className="block">{humanise(q.last_message_purpose ?? '')}</span>
                          </>
                        ) : 'Never'}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {composing && (
        <SendRemindersModal
          recipients={selectedRows}
          templates={templates ?? []}
          withPending={withPending}
          onClose={() => setComposing(false)}
          onDone={() => { setComposing(false); setSelected(new Set()); reload(); }}
        />
      )}
    </div>
  );
}

function SendRemindersModal({ recipients, templates, withPending, onClose, onDone }: {
  recipients: ReminderCandidate[];
  templates: MessageTemplate[];
  withPending: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  // Pre-select whatever the queue suggested for the first recipient.
  const [templateId, setTemplateId] = useState(
    recipients[0]?.suggested_template_id ?? templates[0]?.id ?? '',
  );
  const [customBody, setCustomBody] = useState('');
  const [preview, setPreview] = useState<{ body: string; segments: number; to: string } | null>(null);

  /*
   * How this batch will actually travel. A tenant who asked for email only is
   * not texted, and one with no email address and an "email only" preference
   * is not reached at all — which the manager should know before pressing
   * send, not afterwards from the results.
   */
  const reach = (r: ReminderCandidate) => {
    const pref = r.contact_preference ?? 'both';
    const sms = (pref === 'both' || pref === 'sms') && Boolean(r.phone);
    const email = (pref === 'both' || pref === 'email') && Boolean(r.email);
    return { sms, email, none: !sms && !email };
  };
  const textedCount = recipients.filter((r) => reach(r).sms).length;
  const emailedCount = recipients.filter((r) => reach(r).email && !reach(r).sms).length;
  const unreachableCount = recipients.filter((r) => reach(r).none).length;
  const [loadingPreview, setLoadingPreview] = useState(false);

  async function doPreview() {
    if (recipients.length === 0) return;
    setLoadingPreview(true);
    try {
      const res = await api.post<{ body: string; segments: number; to: string }>('/messages/preview', {
        tenant_id: recipients[0].tenant_id,
        template_id: templateId || undefined,
        body: templateId ? undefined : customBody,
      });
      setPreview(res);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not build the preview');
    } finally {
      setLoadingPreview(false);
    }
  }

  async function send() {
    setBusy(true);
    try {
      const res = await api.post<{ sent: number; skipped: number; failed: number }>('/messages/send', {
        tenant_ids: recipients.map((r) => r.tenant_id),
        template_id: templateId || undefined,
        body: templateId ? undefined : customBody,
        purpose: 'rent_reminder',
      });
      if (res.sent > 0) {
        toast.success(`${res.sent} reminder${res.sent === 1 ? '' : 's'} sent`);
      }
      if (res.skipped > 0) {
        toast.info(`${res.skipped} logged but not sent — text messaging is not enabled`);
      }
      if (res.failed > 0) {
        toast.error(`${res.failed} could not be delivered`);
      }
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the reminders');
    } finally {
      setBusy(false);
    }
  }

  const canSend = Boolean(templateId || customBody.trim());
  const totalOwing = recipients.reduce((sum, r) => sum + r.balance_cents, 0);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Send ${recipients.length} reminder${recipients.length === 1 ? '' : 's'}`}
      subtitle={`${money(totalOwing, currency)} outstanding across these tenants`}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void doPreview()} loading={loadingPreview} disabled={!canSend || busy}>
            Preview
          </Button>
          <Button variant="primary" onClick={() => void send()} loading={busy} disabled={!canSend}
            icon={<Send className="h-4 w-4" />}>
            Send now
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {withPending > 0 && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-warn/10 border border-warn/25">
            <Info className="h-4 w-4 text-warn shrink-0 mt-0.5" />
            <p className="text-sm text-muted">
              <span className="text-ink font-medium">
                {withPending} of these tenants has a payment awaiting your verification.
              </span>{' '}
              They may well have paid already.
            </p>
          </div>
        )}

        <Field label="Template" hint="Each tenant gets their own name, amount and due date filled in">
          <Select
            value={templateId}
            onChange={(e) => { setTemplateId(e.target.value); setPreview(null); }}
          >
            <option value="">Write my own message</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
            ))}
          </Select>
        </Field>

        {!templateId && (
          <Field
            label="Message"
            hint="Placeholders: {{tenant_name}}, {{amount_due}}, {{due_date}}, {{property}}, {{room}}, {{balance}}, {{org_name}}"
          >
            <Textarea
              value={customBody}
              onChange={(e) => { setCustomBody(e.target.value); setPreview(null); }}
              rows={4}
              placeholder="Hi {{tenant_name}}, a reminder that {{amount_due}} is due on {{due_date}}."
            />
          </Field>
        )}

        {preview && (
          <div className="p-4 rounded-xl bg-raised border border-line">
            <p className="label mb-2">
              Preview — as {recipients[0]?.tenant_name} would receive it
            </p>
            <p className="text-sm text-ink whitespace-pre-wrap">{preview.body}</p>
            {/*
              Only the tenants who are actually texted cost segments. Counting
              every recipient overstated the bill the moment a send could go by
              email instead — and quietly implied everyone gets a text.
            */}
            <p className="text-xs text-faint mt-3">
              {preview.body.length} characters · {preview.segments} segment{preview.segments === 1 ? '' : 's'} per text ·{' '}
              {preview.segments * textedCount} total across {textedCount} text{textedCount === 1 ? '' : 's'}
            </p>
            {emailedCount > 0 && (
              <p className="text-xs text-faint mt-1">
                {emailedCount} of these {emailedCount === 1 ? 'is' : 'are'} emailed rather than
                texted, because that is what they asked for. Their copy carries a link to
                their tenant account.
              </p>
            )}
            {unreachableCount > 0 && (
              <p className="text-xs text-warn mt-1">
                {unreachableCount} cannot be reached: no email address on file, or they
                have asked not to be contacted.
              </p>
            )}
          </div>
        )}

        <div>
          <p className="label mb-2">Recipients</p>
          <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
            {recipients.map((r) => {
              const via = reach(r);
              return (
                <div key={r.tenant_id} className="flex items-center gap-2.5 text-sm py-1">
                  <Avatar name={r.tenant_name} size="sm" />
                  <span className="flex-1 truncate">{r.tenant_name}</span>
                  {/* Where it goes, next to who it goes to. */}
                  {via.none ? (
                    <Badge tone="warn">Cannot reach</Badge>
                  ) : (
                    <span className="text-xs text-faint truncate max-w-[180px]">
                      {via.sms && via.email ? `${r.phone} · ${r.email}`
                        : via.sms ? r.phone : r.email}
                    </span>
                  )}
                  <span className="tnum text-xs font-medium">
                    {r.balance_cents > 0 ? money(r.balance_cents, currency) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
